import classNames from 'classnames'
import styles from './DonationListItem.module.css'
import { CSSProperties, ForwardedRef, ReactNode, forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { Card } from '../Card'
import { DonationListItemProps, DonationProps } from './types'
import { IconButton } from '../IconButton'
import { LayoutStack } from '../layouts'
import { LoadingSkeleton } from '../LoadingSkeleton'
import { TextLink } from '../TextLink'
import { useResizeObserver } from '../../hooks'

function Skeleton({ className }: { className?: string }) {
  return (
    <Card className={classNames(styles.card, className)}>
      <LoadingSkeleton width="6.25rem" height="2.82rem" />
      <LayoutStack space="200">
        <LoadingSkeleton width="5.7rem" height="1.25rem" />
        <LoadingSkeleton width="5.7rem" height="1.05rem" />
      </LayoutStack>
    </Card>
  )
}

function Cta({
  className,
  amount,
  ctaText,
  ctaLink,
}: {
  className?: string
  amount: ReactNode
  ctaText: string
  ctaLink: string
}) {
  return (
    <Card className={classNames(styles.card, className)}>
      <div className={classNames(styles.amountContainer, styles.ctaAmountContainer)}>{amount}</div>
      <div className={styles.ctaContainer}>
        <TextLink href={ctaLink}>{ctaText}</TextLink>
      </div>
    </Card>
  )
}

function Comment({
  comment,
  showMoreLabel,
  showLessLabel,
  maxPreviewLines = 2,
}: {
  comment: string
  showMoreLabel: string
  showLessLabel: string
  maxPreviewLines?: number
}) {
  const [showMore, setShowMore] = useState(false)
  const [isTooLong, setIsTooLong] = useState(false)
  const label = showMore ? showLessLabel : showMoreLabel
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) {
      setIsTooLong(false)
      return
    }

    const lineHeight = parseFloat(getComputedStyle(element).lineHeight)
    const neededLines = Math.ceil(element.scrollHeight / lineHeight)

    setIsTooLong(neededLines > maxPreviewLines)
  }, [maxPreviewLines, setIsTooLong])

  const toggleShowMore = useCallback(() => {
    setShowMore((prev) => !prev)
  }, [])
  return (
    <div className={styles.commentContainer}>
      <div
        ref={ref}
        className={classNames(styles.comment, { [styles.expanded!]: showMore })}
        style={{ '--max-preview-lines': maxPreviewLines } as React.CSSProperties}
      >
        {comment}
      </div>
      {isTooLong && (
        <IconButton
          kind="plain"
          size="small"
          iconName={showMore ? 'chevronUp' : 'chevronDown'}
          iconPosition="right"
          onClick={toggleShowMore}
          className={styles.showMoreButton}
        >
          {label}
        </IconButton>
      )}
    </div>
  )
}

function DonationListItemInner({
  amount,
  heading,
  timestamp,
  comment,
  showMoreLabel,
  showLessLabel,
  maxPreviewLines,
  className,
}: DonationProps) {
  return (
    <Card className={classNames(styles.card, className)}>
      <div className={styles.amountContainer}>
        <div>{amount}</div>
      </div>
      <LayoutStack space="none">
        <div className={styles.heading}>{heading}</div>
        <div className={styles.timestamp}>{timestamp}</div>
        {comment && (
          <Comment
            comment={comment}
            showMoreLabel={showMoreLabel}
            showLessLabel={showLessLabel}
            maxPreviewLines={maxPreviewLines}
          />
        )}
      </LayoutStack>
    </Card>
  )
}

const DonationListItemWithHeightObserver = forwardRef(function DonationListItemWithHeightObserver(
  {
    amount,
    heading,
    timestamp,
    comment,
    showMoreLabel,
    showLessLabel,
    maxPreviewLines,
    className,
    style,
    onChangeSize,
  }: DonationProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const localRef = useRef<HTMLDivElement>(null)
  const heightRef = useRef<number | null>(null)
  if (style?.height) {
    const height = parseFloat(style.height)
    heightRef.current = height
  }

  const handleSizeChange = useCallback(
    (height: number | undefined) => {
      if (!onChangeSize || !height) return

      const flooredHeight = Math.floor(height)
      if (typeof heightRef.current === 'number' && heightRef.current > 0 && flooredHeight !== heightRef.current) {
        onChangeSize(flooredHeight)
        heightRef.current = flooredHeight
      }
    },
    [onChangeSize]
  )

  useResizeObserver({
    ref: localRef,
    box: 'border-box',
    onResize: ({ height }) => handleSizeChange(height),
  })

  return (
    <div style={{ ...(style as CSSProperties), overflow: 'visible' }} ref={ref}>
      <Card className={classNames(styles.card, className)} ref={localRef}>
        <div className={styles.amountContainer}>
          <div>{amount}</div>
        </div>
        <LayoutStack space="none">
          <div className={styles.heading}>{heading}</div>
          <div className={styles.timestamp}>{timestamp}</div>
          {comment && (
            <Comment
              comment={comment}
              showMoreLabel={showMoreLabel}
              showLessLabel={showLessLabel}
              maxPreviewLines={maxPreviewLines}
            />
          )}
        </LayoutStack>
      </Card>
    </div>
  )
})

const DonationListItem = forwardRef(function DonationListItem(
  { variant, amount, ctaText, ctaLink, className, ...props }: DonationListItemProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  if (variant === 'skeleton') return <Skeleton className={className} />
  if (variant === 'cta') return <Cta amount={amount} ctaText={ctaText} ctaLink={ctaLink} className={className} />

  const donationProps = { variant, amount, ctaText, ctaLink, className, ...props } as DonationProps

  const shouldObserveSize = props.onChangeSize
  if (shouldObserveSize) return <DonationListItemWithHeightObserver ref={ref} {...donationProps} />

  return <DonationListItemInner {...donationProps} />
})

export default DonationListItem
